import React, { useEffect } from 'react';
import { HiCheck, HiX, HiExclamation, HiInformationCircle } from 'react-icons/hi';

const Toast = ({ 
  id, 
  type = 'success', 
  message, 
  duration = 5000, 
  onRemove 
}) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onRemove(id);
    }, duration);

    return () => clearTimeout(timer);
  }, [id, duration, onRemove]);

  const getToastStyles = () => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-white',
          border: 'border-green-200',
          text: 'text-gray-800',
          icon: 'text-white',
          iconBg: 'bg-green-500',
          shadow: 'shadow-lg shadow-green-100/50',
          accent: 'border-l-4 border-l-green-500'
        };
      case 'error':
        return {
          bg: 'bg-white',
          border: 'border-action/20',
          text: 'text-gray-800',
          icon: 'text-white',
          iconBg: 'bg-action',
          shadow: 'shadow-lg shadow-action/10',
          accent: 'border-l-4 border-l-action'
        };
      case 'warning':
        return {
          bg: 'bg-white',
          border: 'border-yellow-200',
          text: 'text-gray-800',
          icon: 'text-white',
          iconBg: 'bg-yellow-500',
          shadow: 'shadow-lg shadow-yellow-100/50',
          accent: 'border-l-4 border-l-yellow-500'
        };
      case 'info':
        return {
          bg: 'bg-white',
          border: 'border-primary-200',
          text: 'text-gray-800',
          icon: 'text-white',
          iconBg: 'bg-primary-500',
          shadow: 'shadow-lg shadow-primary-100/50',
          accent: 'border-l-4 border-l-primary-500'
        };
      default:
        return {
          bg: 'bg-white',
          border: 'border-gray-200',
          text: 'text-gray-800',
          icon: 'text-white',
          iconBg: 'bg-primary-500',
          shadow: 'shadow-lg shadow-gray-100/50',
          accent: 'border-l-4 border-l-primary-500'
        };
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <HiCheck className="w-4 h-4" />;
      case 'error':
        return <HiX className="w-4 h-4" />;
      case 'warning':
        return <HiExclamation className="w-4 h-4" />;
      case 'info':
        return <HiInformationCircle className="w-4 h-4" />;
      default:
        return <HiInformationCircle className="w-4 h-4" />;
    }
  };

  const styles = getToastStyles();

  return (
    <div
      className={`
        ${styles.bg} ${styles.border} ${styles.text} ${styles.shadow} ${styles.accent}
        relative flex items-center p-4 rounded-lg border-2
        transform transition-all duration-300 ease-in-out
        animate-in slide-in-from-right-full
        max-w-sm w-full backdrop-blur-sm
        hover:shadow-xl hover:scale-[1.02] transition-all duration-200
      `}
      role="alert"
    >
      {/* Icon */}
      <div className={`${styles.iconBg} ${styles.icon} flex-shrink-0 rounded-full p-1.5 mr-3 flex items-center justify-center`}>
        {getIcon()}
      </div>
      
      {/* Message */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium font-flex-regular leading-relaxed">
          {message}
        </p>
      </div>
      
      {/* Close Button */}
      <button
        onClick={() => onRemove(id)}
        className={`
          text-gray-400 hover:text-gray-600 hover:bg-gray-100
          flex-shrink-0 ml-2 p-1.5 rounded-full
          transition-all duration-200 hover:scale-110
        `}
        aria-label="Close notification"
      >
        <HiX className="w-4 h-4" />
      </button>
    </div>
  );
};

export default Toast;
